import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import axios from "axios";
import { AUTH_URL } from "../../utils/constants";
import { toSentenceCase } from "../../utils/utils";

// Using the exact logo provided in the requirements
import Logo from "../../assets/images/image copy.png";

const Header = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);
  const notificationsDropdown = () => setNotificationsOpen((prev) => !prev);

  useEffect(() => {
    if (!isAuthenticated) return;
    let endpoint = "";
    if (user?.role === "officer") {
      endpoint = `api/officer_receives_notification?officer_name=${encodeURIComponent(user.category)}`;
    } else if (user?.role === "admin") {
      endpoint = `api/admin_receives_notification`;
    } else {
      endpoint = `api/user_notification_status?user_id=${user.userId}`;
    }
    const getNotifiaction = async () => {
      try {
        const res = await axios.get(`${AUTH_URL}${endpoint}`, {
          headers: { "Content-Type": "application/json" },
        });
        const raw = res.data?.Final_notification_message || [];
        // Each persistent notification has its own _id, so repeated admin
        // actions (e.g. two reminders, or a reminder + BTR approval) must all
        // be shown. Only drop exact repeats of the same record. (Previously
        // this keyed on grievance + message, which collapsed repeated
        // reminders into one.)
        const seen = new Map();
        raw.forEach(n => {
          const key = `${n._id || n.id || ''}|${n.type || n.event_type || n.title || ''}|${n.message || ''}`;
          if (!seen.has(key)) seen.set(key, n);
        });
        const list = Array.from(seen.values()).sort(
          (a, b) => (new Date(b.created_at).getTime() || 0) - (new Date(a.created_at).getTime() || 0)
        );
        setNotifications(list);
      } catch (error) {
        console.log("No records");
      }
    };
    getNotifiaction();
    const pollInterval = setInterval(getNotifiaction, 30000);
    return () => clearInterval(pollInterval);
  }, [isAuthenticated, user]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full h-[60px] sm:h-[80px] md:h-[100px] lg:h-[15vh] bg-brand-teal text-white flex-shrink-0 z-50 shadow-md border-b-[4px] border-navy-dark relative flex items-center justify-between px-4 sm:px-6">
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 10px 10px, white 2px, transparent 0)", backgroundSize: "32px 32px" }}></div>
      
      {/* Logo Section */}
      <div className="flex items-center gap-3 sm:gap-4 relative z-10 cursor-pointer" onClick={() => navigate("/")}>
        <div className="bg-white rounded-[18px] p-[6px] shadow-[0_3px_10px_rgba(0,0,0,0.1)] w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] md:w-[78px] md:h-[78px] flex items-center justify-center overflow-hidden border-[2px] border-[#e07a2c]/35 flex-shrink-0">
          <img src={Logo} alt="NCW Logo" className="w-full h-full object-contain" />
        </div>
      </div>

      {/* Center Text Section */}
      <div className="absolute left-1/2 transform -translate-x-1/2 w-[55%] sm:w-auto z-0 flex flex-col items-center justify-center text-center cursor-pointer" onClick={() => navigate("/")}>
        <h1 className="font-heading font-bold text-lg sm:text-2xl lg:text-3xl leading-tight tracking-wide drop-shadow-sm whitespace-nowrap">NCW Complaint Redressal Portal</h1>
        <h2 className="font-sans text-[8px] sm:text-[10px] lg:text-[12px] uppercase tracking-widest text-cream opacity-90 mt-0.5 whitespace-nowrap hidden sm:block">AI-Powered Complaint Redressal · National Commission for Women</h2>
      </div>

      {/* Action Section */}
      <div className="flex items-center gap-4 sm:gap-6 relative z-10">
        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          {(() => {
            const unreadCount = (notifications || []).filter(n => !n.is_read).length;
            return (
              <>
                <button onClick={notificationsDropdown} className="relative w-9 h-9 rounded-full bg-white text-brand-teal flex items-center justify-center hover:bg-cream transition-colors shadow-sm">
                  <i className="fa-regular fa-bell text-lg"></i>
                  {unreadCount > 0 && (
              <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 bg-saffron text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border-2 border-brand-teal">
                {unreadCount}
              </span>
            )}
          </button>
          {notificationsOpen && (
            <div className="absolute right-0 mt-3 w-72 sm:w-80 bg-white rounded-lg shadow-xl z-50 text-black border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                <h3 className="font-bold text-navy-dark text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-saffron/10 text-saffron px-2 py-0.5 rounded-full font-bold">{unreadCount} new</span>
                    <button
                      type="button"
                      className="text-[11px] text-brand-teal font-semibold hover:underline"
                      onClick={() => {
                        const ids = notifications.filter(n => !n.is_read && (n._id || n.id)).map(n => n._id || n.id);
                        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                        if (ids.length) {
                          axios.put(`${AUTH_URL}api/notifications/mark_read`, { notification_ids: ids })
                            .catch(err => console.error("Failed to mark notifications as read", err));
                        }
                      }}
                    >
                      Mark all read
                    </button>
                  </div>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {(!notifications || notifications.length === 0) ? (
                  <div className="p-8 text-center flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mb-1"><i className="fa-regular fa-bell-slash text-xl"></i></div>
                    <p className="text-sm text-gray-500 font-medium">No new notifications</p>
                  </div>
                ) : (
                  notifications.map((item, idx) => {
                    const getComplaintId = (notif) => {
                      if (notif.grievance_id) return notif.grievance_id;
                      if (notif.complaint_id) return notif.complaint_id;
                      const match = (notif.title + " " + notif.message).match(/NCW\d+/i);
                      return match ? match[0] : null;
                    };
                    return (
                    <div 
                      key={`${item._id || "n"}-${item.type || item.title || ""}-${idx}`} 
                      className={`p-4 border-b border-gray-50 hover:bg-page-bg transition-colors cursor-pointer ${!item.is_read ? 'bg-cream/30' : ''}`}
                      onClick={() => {
                        setNotificationsOpen(false);
                        
                        // Mark as read locally and hit API
                        if (!item.is_read) {
                          setNotifications(prev => prev.map(n =>
                            (n === item) ? { ...n, is_read: true } : n
                          ));
                          if (item._id || item.id) {
                            axios.put(`${AUTH_URL}api/notifications/mark_read`, { notification_ids: [item._id || item.id] })
                              .catch(err => console.error("Failed to mark notification as read", err));
                          }
                        }

                        const cid = getComplaintId(item);
                        let path = "/desk";
                        if (user?.role === "officer") path = "/officerDesk";
                        else if (user?.role === "admin") path = "/adminDesk";
                        
                        if (cid) {
                          navigate(`${path}?search=${cid}`);
                        } else {
                          navigate(path);
                        }
                      }}
                    >
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="fa-solid fa-envelope-open-text text-sm"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-sm text-gray-800 leading-snug">{item.title}</h4>
                          <p className="text-xs text-gray-600 mt-1 leading-relaxed line-clamp-2">{item.message}</p>
                          <p className="text-[10px] text-gray-400 mt-2 font-medium"><i className="fa-regular fa-clock mr-1"></i>{new Date(item.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )})
                )}
              </div>
            </div>
          )}
          </>
          );
        })()}
        </div>

        {isAuthenticated && (
          <div className="relative" ref={dropdownRef}>
            <button onClick={toggleDropdown} className="flex items-center gap-2.5 px-1.5 py-1.5 pr-4 rounded-full bg-white text-navy-dark hover:bg-cream transition-all shadow-sm">
              <div className="w-8 h-8 rounded-full bg-navy-dark text-white flex items-center justify-center font-bold text-sm">
                {user?.role === "admin" ? "A" : user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="hidden sm:inline font-medium text-sm">
                {user?.role === "admin" ? "Admin" : toSentenceCase(user?.name) || "User"}
              </span>
              <i className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 opacity-60 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            
            {dropdownOpen && (
              <div className="absolute right-0 mt-3 w-56 bg-white rounded-lg shadow-xl z-50 text-black border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Signed in as</p>
                  <p className="text-sm font-bold text-navy-dark truncate mt-0.5">{user?.email || "user@example.com"}</p>
                  <p className="text-xs text-gray-500 mt-1 capitalize font-medium px-2 py-0.5 bg-gray-200 inline-block rounded">Role: {user?.role || "user"}</p>
                </div>
                <div className="py-1">
                  <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm text-brand-teal hover:bg-cream transition-colors flex items-center gap-2.5 font-semibold">
                    <i className="fa-solid fa-arrow-right-from-bracket w-4 text-center"></i> Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
